// src/Components/LeaveGameButton.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import gameControlService from "../Services/gameControlService";

const LeaveGameButton = ({ roomData, roomDocId }) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const navigate = useNavigate();

  const currentUserId = localStorage.getItem("currentUserId");
  const currentRoomId = localStorage.getItem("currentRoomId");
  const currentPlayerName = localStorage.getItem("playerName");

  const handleLeaveClick = () => {
    if (!roomData || !currentUserId) return;
    setShowConfirmation(true);
  };

  const handleConfirmLeave = async () => {
    if (!roomData || !currentUserId) return;

    setIsLeaving(true);

    try {
      const leaveInfo = gameControlService.canPlayerLeave(roomData, currentUserId);

      if (leaveInfo.willEndGame) {
        // End game for everyone if only 2 players left
        const endResult = await gameControlService.endGameForAll(
          roomDocId,
          currentUserId,
          "player_left"
        );

        if (endResult.success) {
          navigate("/");
          return;
        }
      }

      // Normal leave
      const leaveResult = await gameControlService.leaveRoom(
        currentRoomId,
        currentUserId,
        currentPlayerName
      );

      if (leaveResult.success) {
        navigate("/");
      } else {
        alert(leaveResult.message || "Failed to leave game");
        setIsLeaving(false);
        setShowConfirmation(false);
      }
    } catch (error) {
      console.error("Error leaving game:", error);
      alert("Failed to leave game. Please try again.");
      setIsLeaving(false);
      setShowConfirmation(false);
    }
  };

  const getConfirmationMessage = () => {
    if (!roomData) return "Are you sure you want to leave the game?";

    const leaveInfo = gameControlService.canPlayerLeave(roomData, currentUserId);

    if (leaveInfo.willEndGame) {
      return "Warning: Only 2 players remaining. If you leave, the game will end for everyone. Continue?";
    }

    if (leaveInfo.isHost) {
      return "As the host, leaving will remove you from the game but others can continue. Continue?";
    }

    return "Are you sure you want to leave the game? Other players will continue without you.";
  };

  // Confirmation Modal
  const ConfirmationModal = () => {
    if (!showConfirmation) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full">
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-2">Leave Game</h2>
            <p className="text-gray-600 mb-6">{getConfirmationMessage()}</p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowConfirmation(false)}
                disabled={isLeaving}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition duration-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmLeave}
                disabled={isLeaving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLeaving ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Leaving...
                  </span>
                ) : (
                  "Leave Game"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <button
        onClick={handleLeaveClick}
        className="bg-gray-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-gray-700 transition duration-300 text-sm"
        title="Leave Game"
      >
        Leave Game
      </button>
      <ConfirmationModal />
    </>
  );
};

export default LeaveGameButton;